"use strict";
/**
 * StyleSheet allows javascript to add CSS to a stylesheet tag. This should be used when setting
 * css properties on DOM nodes through javascript does not work, e.g. animations and media queries.
 *
 * Note that the some methods take CSS props as string dictionaries. Write the props and vals as you
 * would in CSS, not as you would in javascript.
 *
 *   Right: { 'animation-duration': '1s' }
 *   Wrong: { animationDuration: 1000 }
 */
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var CssIdPrefix = 'shared-widgets';
var StyleSheet = /** @class */ (function () {
    function StyleSheet(sheetElementIdPostfix) {
        this.idCounter = 1;
        this.classNameLookup = {};
        this.rawSelectorLookup = {};
        // If running within Node, there is no DOM
        if (typeof document === 'undefined') {
            return;
        }
        this.initializeStyleSheet(sheetElementIdPostfix);
    }
    /**
     * Adds a raw selector to the style sheet with the given rules.
     */
    StyleSheet.prototype.addRawSelector = function (selector, cssLines) {
        var ruleBody = this.toRuleBody(cssLines);
        if (selector in this.rawSelectorLookup) {
            throw new Error(selector + " has already been defined.");
        }
        this.rawSelectorLookup[selector] = true;
        this.addRule(selector + " {" + ruleBody + "}");
    };
    /**
     * Adds a class for the given properties and values, returning the class name. It will create a class name if not
     * given one. If a class with the same body was previously created using this StyleSheet, returns the existing
     * class name. Will throw if the existing class name is different than the passed in class name.
     */
    StyleSheet.prototype.addClass = function (cssLines, classNameToUse) {
        var ruleBody = this.toRuleBody(cssLines);
        // If we already defined these exact rules, use the existing class name
        var existingClassName = this.classNameLookup[ruleBody];
        if (existingClassName) {
            if (classNameToUse && classNameToUse !== existingClassName) {
                throw new Error("Attempting to define css rule using class name '" + classNameToUse + "', but a class with the given\n           rule body already exists under class name '" + existingClassName + "'. Rule body: " + ruleBody + ".");
            }
            return existingClassName;
        }
        if (!classNameToUse) {
            classNameToUse = CssIdPrefix + "_" + this.idCounter++;
        }
        // Cache this so we can look it up latter
        this.classNameLookup[ruleBody] = classNameToUse;
        this.addRule("." + classNameToUse + " {" + ruleBody + "}");
        return classNameToUse;
    };
    /**
     * Add an animation, returning the class name for the animation. It will create a class name if not given one.
     * If an animation with the same definition (deep comparison) was previously created using this StyleSheet, returns
     * the existing class name. Will throw if the existing class name is different than the passed in class name.
     *
     * Warning: This is a leak at the moment. An animation may be temporary, but it will never get removed
     *
     * Warning: IE through 11 does not support CSS calc() inside animations. This means that if your animation
     * depends on width or height of a DOM node, you must know the pixel value of that width or height when
     * defining the animation.
     * https://connect.microsoft.com/IE/feedback/details/733978/css-animation-dont-accept-calc-as-property-values
     */
    StyleSheet.prototype.addAnimation = function (animationDefinition, classNameToUse) {
        var options = animationDefinition.options;
        var classNameLookupKey = JSON.stringify(animationDefinition);
        // If we already defined this exact animation, use the existing class name
        var existingClassName = this.classNameLookup[classNameLookupKey];
        if (existingClassName) {
            if (classNameToUse && classNameToUse !== existingClassName) {
                throw new Error("Attempting to define css animation using class name '" + classNameToUse + "', but a css rule with that\n           definition already exists under class name '" + existingClassName + "'. Definition: " + animationDefinition);
            }
            return existingClassName;
        }
        // Create the new animation
        var keyframeId = this.addKeyframes(animationDefinition.keyframes);
        var className = this.addClass({
            'animation-duration': options.durationMs / 1000 + "s",
            'animation-timing-function': options.timingFunction,
            'animation-iteration-count': "" + (options.count === Infinity ? 'infinite' : options.count),
            'animation-direction': options.direction,
            'animation-name': keyframeId,
        }, classNameToUse);
        this.classNameLookup[classNameLookupKey] = className;
        return className;
    };
    StyleSheet.prototype.dispose = function () {
        var styleNode = this.styleSheet.ownerNode;
        styleNode.parentElement.removeChild(styleNode);
    };
    /**
     * Add keyframe. Returns the generated keyframe id.
     */
    StyleSheet.prototype.addKeyframes = function (_a) {
        var _this = this;
        var fromCSSLines = _a.fromCSSLines, toCSSLines = _a.toCSSLines, _b = _a.tweenFrames, tweenFrames = _b === void 0 ? [] : _b;
        var keyframesId = CssIdPrefix + "_" + this.idCounter++;
        var keyframes = tslib_1.__spreadArrays([
            { percent: 0, cssLines: fromCSSLines },
            { percent: 100, cssLines: toCSSLines }
        ], tweenFrames);
        var keyframeStrings = keyframes.map(function (keyframe) {
            return keyframe ? keyframe.percent + "% { " + _this.toRuleBody(keyframe.cssLines) + " }" : '';
        });
        this.addRule("@keyframes " + keyframesId + " { " + keyframeStrings.join(' ') + "}");
        return keyframesId;
    };
    /**
     * Convert a string dict into the body of a css rule (no curlies included)
     */
    StyleSheet.prototype.toRuleBody = function (cssLines) {
        return Object.keys(cssLines).map(function (prop) {
            if (prop.toLowerCase() !== prop) {
                throw new Error("The CSS prop '" + prop + "' should be written kebab-case, not camelCase");
            }
            return prop + ": " + cssLines[prop] + ";";
        }).join('');
    };
    StyleSheet.prototype.addRule = function (rule) {
        try {
            this.styleSheet.insertRule(rule, this.styleSheet.cssRules.length);
        }
        catch (error) {
            throw new Error(error + ". Stylesheet injection of " + rule + " failed.  This browser may not support the given CSS rule or selector");
        }
    };
    StyleSheet.prototype.initializeStyleSheet = function (sheetElementIdSuffix) {
        var styleElementId = "tableau-widgets-appendedCustomStyle-" + sheetElementIdSuffix;
        // Create a new style node to be used by shared widgets
        var styleElement = document.createElement('style');
        styleElement.setAttribute('type', 'text/css');
        styleElement.id = styleElementId;
        // For older versions of Safari, it is necessary to add an empty text node in order for the style to be
        // actually added into the DOM.
        styleElement.appendChild(document.createTextNode(''));
        document.head.appendChild(styleElement);
        // Cache the CSSStyleSheet so we can add rules to it
        this.styleSheet = document.getElementById(styleElementId).sheet;
    };
    return StyleSheet;
}());
exports.StyleSheet = StyleSheet;
var SingletonStyleSheet = new StyleSheet('singleton');
exports.SingletonStyleSheet = SingletonStyleSheet;
//# sourceMappingURL=StyleSheet.js.map